/**
 * Unit tab · Parts. What wears out and exactly what to buy: grouped by room, filtered by type.
 */
import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { useCustomPartTypes } from "@/hooks/usePropertyParts";
import { PART_TYPES, partSummary, partTypeDef, replaceEveryLabel, type PropertyPart } from "@/lib/parts";
import { useInventoryItems } from "@/hooks/useInventory";

export function UnitPartsPage({
  propertyId,
  parts,
  onAdd,
  onEdit,
}: {
  propertyId: string;
  parts: PropertyPart[];
  onAdd: () => void;
  onEdit: (p: PropertyPart) => void;
}) {
  const { data: customTypes = [] } = useCustomPartTypes();
  const { data: inv = [] } = useInventoryItems(propertyId);
  const [filter, setFilter] = useState<string>("all");

  const spareOf = (p: PropertyPart): string | null => {
    if (!p.cleaner_can_replace) return null;
    const row = (inv as { id: string; current_count?: number | null; in_stock_qty?: number | null; threshold_count?: number | null }[]).find(
      (i) => i.id === p.spare_inventory_item_id,
    );
    if (!row) return "No spare";
    const n = row.in_stock_qty ?? row.current_count ?? 0;
    if (n <= 0) return "No spare";
    return n <= (row.threshold_count ?? p.keep_spares) ? `Spare: ${n} · low` : `Spare: ${n} in closet`;
  };

  const typeKey = (p: PropertyPart) => (p.custom_type_id ? `c:${p.custom_type_id}` : p.part_type);
  const chips = useMemo(() => {
    const count = (k: string) => parts.filter((p) => typeKey(p) === k).length;
    return [
      { id: "all", label: "All", n: parts.length },
      ...PART_TYPES.filter((t) => count(t.id) > 0 || t.id !== "other").map((t) => ({ id: t.id, label: t.plural, n: count(t.id) })),
      ...customTypes.map((c) => ({ id: `c:${c.id}`, label: c.name, n: count(`c:${c.id}`) })),
    ].filter((c) => c.id === "all" || c.n > 0 || PART_TYPES.some((t) => t.id === c.id && t.id !== "other"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parts, customTypes]);

  const shown = parts.filter((p) => filter === "all" || typeKey(p) === filter);
  const byRoom = useMemo(() => {
    const m = new Map<string, PropertyPart[]>();
    for (const p of shown) {
      const r = p.room || "No room";
      m.set(r, [...(m.get(r) ?? []), p]);
    }
    return [...m.entries()];
  }, [shown]);

  if (parts.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-[24px] bg-white px-6 py-16 text-center">
        <div className="text-[22px] font-bold tracking-[-0.3px] text-[#0f1720]">What wears out</div>
        <div className="max-w-[440px] text-[15px] font-medium text-[#64748b]">
          Bulbs, filters, batteries and remotes, so anyone knows exactly what to buy.
        </div>
        <button type="button" onClick={onAdd} className="mt-2 flex h-12 items-center rounded-[14px] bg-[#15776d] px-6 text-[15px] font-bold text-white">
          Add first part
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[22px] font-bold tracking-[-0.3px] text-[#0f1720]">Parts</div>
          <div className="text-[14px] font-medium text-[#64748b]">What wears out, and exactly what to buy.</div>
        </div>
        <button type="button" onClick={onAdd} className="flex h-11 shrink-0 items-center rounded-[14px] bg-[#15776d] px-4 text-[14.5px] font-bold text-white">
          Add part
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {chips.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setFilter(c.id)}
            className={cn(
              "flex h-10 items-center gap-1.5 rounded-full px-3.5 text-[13.5px] font-bold",
              filter === c.id ? "bg-[#0f1720] text-white" : "bg-white text-[#475569] hover:bg-[#eef3f5]",
            )}
          >
            {c.label}
            <span className={cn("text-[12.5px]", filter === c.id ? "text-white/70" : "text-[#8494a2]")}>{c.n}</span>
          </button>
        ))}
      </div>

      {byRoom.map(([room, list]) => (
        <section key={room} className="flex flex-col gap-2">
          <div className="flex items-baseline gap-2 px-1">
            <div className="text-[17px] font-bold text-[#0f1720]">{room}</div>
            <div className="text-[13px] font-medium text-[#8494a2]">
              {list.length} part{list.length === 1 ? "" : "s"}
            </div>
          </div>
          <div className="overflow-hidden rounded-[22px] bg-white">
            {list.map((p, i) => {
              const spare = spareOf(p);
              const every = replaceEveryLabel(p.replace_every_months);
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => onEdit(p)}
                  className={cn("flex w-full items-center gap-3.5 px-4 py-3.5 text-left hover:bg-[#fafcfc]", i > 0 && "border-t border-[#eef2f5]")}
                >
                  <div className="h-[56px] w-[56px] shrink-0 overflow-hidden rounded-[14px] bg-[#eef2f5]">
                    {p.photo_label_url || p.photo_place_url ? (
                      <img src={(p.photo_place_url || p.photo_label_url) as string} alt="" className="h-full w-full object-cover" />
                    ) : null}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[16px] font-bold tracking-[-0.2px] text-[#0f1720]">{p.name}</div>
                    <div className="truncate text-[13.5px] font-medium text-[#64748b]">
                      {partSummary(p)}
                      {every ? ` · ${every}` : ""}
                    </div>
                    <div className="truncate text-[12.5px] font-medium text-[#8494a2]">
                      {[p.place, [p.brand, p.model].filter(Boolean).join(" ")].filter(Boolean).join(" · ") || partTypeDef(p.part_type).label}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1.5">
                    <span
                      className={cn(
                        "rounded-full px-2.5 py-1 text-[12px] font-bold",
                        p.cleaner_can_replace ? "bg-[#e2eeed] text-[#15776d]" : "bg-[#eef2f5] text-[#64748b]",
                      )}
                    >
                      {p.cleaner_can_replace ? "Cleaner replaces" : "You replace"}
                    </span>
                    {spare ? (
                      <span className={cn("text-[12.5px] font-bold", /No spare|low/.test(spare) ? "text-[#b45309]" : "text-[#64748b]")}>{spare}</span>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
